import { useState, useContext, useMemo } from 'react'
import { Typography, TextField, Box, Tabs, Tab, Button, Stack, Switch } from '@mui/material'

import { enqueueSnackbar } from 'notistack'
import moment from 'moment-timezone'

import WarningAmberIcon from '@mui/icons-material/WarningAmber'

import { AppContext } from '/providers/app-context'
import { toggleScannerPassEnabled, changeLoyaltyPoints } from '/providers/studio-api'
import StyledPaper from '/components/paper'
import ActionErrorDialog from '/components/action-error-dialog'
import useActionErrorDialog from '/components/use-action-error-dialog'

const PassEditorStamp = ({ pass, template, setPass }) => {

  const [tabIndex, setTabIndex] = useState("add")

  return (
    <div className='h-full pb-32'>
      <StyledPaper>
        <Typography variant="h6" sx={{ p: 2 }}>Loyalty Card Actions</Typography>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', marginBottom: 1 }}>
          <Tabs value={tabIndex} onChange={(e, value) => setTabIndex(value)}>
            <Tab label="Add points" value="add" />
            <Tab label="Redeem points" value="remove" />
            <Tab label="Manage" value="manage" />
          </Tabs>
        </Box>
      </StyledPaper>

      <div className='overflow-y-scroll flex flex-col h-full pb-32 items-center justify-around'>
        <StyledPaper>
          <div className='p-5'>
            <AddTab tabIndex={tabIndex} index="add" pass={pass} template={template} setPass={setPass} />
            <RedeemTab tabIndex={tabIndex} index="remove" pass={pass} template={template} setPass={setPass} />
            <ManageTab tabIndex={tabIndex} index="manage" pass={pass} templateId={template.template_id} setPass={setPass} />
          </div>
        </StyledPaper>
      </div>
    </div>
  )
}

export default PassEditorStamp

const AddTab = ({ tabIndex, index, pass, template, setPass }) => {

  const { apiSource, apiKey, setProcessing } = useContext(AppContext)
  const [amount, setAmount] = useState("")
  const [note, setNote] = useState("")
  const { actionError, errorDialogOpen, setErrorDialogOpen, showActionError } = useActionErrorDialog()

  const save = async () => {
    setProcessing(true)
    const result = await changeLoyaltyPoints({ apiSource, apiKey, templateId: template.template_id, passId: pass.pass_id, action: "add", amount: parseInt(amount, 10), note })
    setProcessing(false)

    if (result.error) {
      showActionError(result.message)
    } else {
      setAmount("")
      setNote("")
      setPass(result)
      enqueueSnackbar({ message: 'Points added', variant: 'success' })
    }
  }

  if (tabIndex === index) return <>
    <NumericInput template={template} pass={pass} note={note} setNote={setNote} amount={amount} setAmount={setAmount} label="Number of points to add" submitText="Add" save={save} />
    <ActionErrorDialog title="Loyalty update failed" open={errorDialogOpen} setOpen={setErrorDialogOpen} error={actionError} />
  </>
}

const RedeemTab = ({ tabIndex, index, pass, template, setPass }) => {

  const { apiSource, apiKey, setProcessing } = useContext(AppContext)
  const [amount, setAmount] = useState("")
  const [note, setNote] = useState("")
  const { actionError, errorDialogOpen, setErrorDialogOpen, showActionError } = useActionErrorDialog()

  const save = async () => {
    setProcessing(true)
    const result = await changeLoyaltyPoints({ apiSource, apiKey, templateId: template.template_id, passId: pass.pass_id, action: "redeem", amount: parseInt(amount, 10), note })
    setProcessing(false)

    if (result.error) {
      showActionError(result.message)
    } else {
      setAmount("")
      setNote("")
      setPass(result)
      enqueueSnackbar({ message: 'Points removed', variant: 'success' })
    }
  }

  if (tabIndex === index) return <>
    <NumericInput template={template} pass={pass} note={note} setNote={setNote} amount={amount} setAmount={setAmount} label="Number of points to remove" submitText="Remove" save={save} limitMax={true} />
    <ActionErrorDialog title="Loyalty update failed" open={errorDialogOpen} setOpen={setErrorDialogOpen} error={actionError} />
  </>
}


const regex = /^[0-9]+$/i

const NumericInput = ({ pass, amount, setAmount, note, setNote, label, helperText, submitText, save, limitMax, maxField }) => {

  const disabled = pass.variables.scan.disabled
  const num_points = pass.variables.loyalty.points.balance
  const max = limitMax ? (maxField === "points" ? pass.variables.loyalty.points.balance : num_points) : 99999

  return (
    <div className='flex flex-1 flex-col justify-around items-center'>
      <div>
        <div className='flex flex-col justify-around items-center gap-10'>
          <div>
            <div className='font-semibold text-xl self-start text-gray-400'>Current points : {num_points}</div>
          </div>

          {!disabled &&
            <>
              <TextField
                value={amount}
                onChange={e => {
                  if (e.target.value === '' || regex.test(e.target.value)) {
                    setAmount(e.target.value)
                  }
                }}
                variant='outlined'
                label={label}
                placeholder='0'
                slotProps={{
                  input: {
                    style: { fontSize: 50, width: 300 }
                  },
                  inputLabel: {
                    shrink: true,
                  }
                }}
              />

              <div className='font-semibold text-xl self-start'>{helperText}</div>

              <TextField multiline fullWidth rows={4} label="note" value={note} onChange={e => setNote(e.target.value)} helperText={`Add a note to explain why you are adjusting the loyalty point count.`} slotProps={{
                inputLabel: {
                  shrink: true,
                }
              }}>{note}</TextField>

              <div className='self-end'><Button variant='contained' disabled={amount === "" || (limitMax && amount > max)} onClick={save}>{submitText}</Button></div>

            </>
          }

          {disabled &&
            <div><WarningAmberIcon className='mr-5 text-red-500' />
              Stampcard is disabled and cannot be used
              <br />
              <span className='text-gray-500'>You can change this in the MANAGE section</span>
            </div>
          }

        </div>
      </div>
    </div>
  )
}


const ManageTab = ({ tabIndex, index, pass, templateId, setPass }) => {

  const { apiSource, apiKey, setProcessing } = useContext(AppContext)

  const [enabled, setEnabled] = useState(pass.variables.scan.disabled)
  const [note, setNote] = useState("")
  const { actionError, errorDialogOpen, setErrorDialogOpen, showActionError } = useActionErrorDialog()

  const passCurrentlyEnabled = useMemo(() => pass.variables.scan.disabled, [pass.variables])

  const noteDisabled = useMemo(() => enabled === passCurrentlyEnabled, [enabled, passCurrentlyEnabled])

  const saveEnabledDisabled = async () => {
    setProcessing(true)
    const result = await toggleScannerPassEnabled({ apiSource, apiKey, templateId, passId: pass.pass_id, enabled, note })
    setProcessing(false)

    if (result.error) {
      showActionError(result.error, 'Error updating loyalty card status')
    } else {
      setPass(result)
      setNote("")
      enqueueSnackbar({ message: 'Stampcard  ' + (enabled ? "enabled" : "disabled"), variant: 'success' })
    }
  }

  if (tabIndex === index) return (
    <>
    <div className='flex flex-1 flex-col justify-around items-center'>
      <div>
        <div className='flex flex-col justify-around items-center gap-10'>

          <div style={{ width: 400 }} className='flex flex-col gap-5'>

            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Typography>Stampcard status: Disabled</Typography>
              <Switch checked={enabled} onChange={e => setEnabled(e.target.checked)} />
              <Typography>Enabled</Typography>
            </Stack>

            {!enabled && <div className='mt-5 mb-5 '>Disabled: {formatDate(pass.variables.scan.disabled_date)}</div>}

            <TextField multiline fullWidth rows={4} label="Note" onChange={e => setNote(e.target.value)} disabled={noteDisabled} helperText={`Add a note to explain why you are ${passCurrentlyEnabled ? "disabling" : "enabling"} this stampcard `} slotProps={{
              inputLabel: {
                shrink: true,
              }
            }}>{note}</TextField>
            <div className='self-end'>
              <Button variant='contained' onClick={saveEnabledDisabled}>Save</Button>
            </div>
          </div>
        </div>

      </div>
    </div>
    <ActionErrorDialog title="Loyalty update failed" open={errorDialogOpen} setOpen={setErrorDialogOpen} error={actionError} />
    </>
  )
}

const formatDate = ymd => moment(ymd, "YYYYMMDDHHmmSS").format("DD MMM YYYY HH:mm") 
